import { useState } from "react";
import { showConfirm } from "../lib/dialog";
import {
  getAhrefsKey, getAnthropicKey, getDfsCreds, getBrightDataCreds,
  saveAhrefsKey, saveAnthropicKey, saveDfsCreds, saveBrightDataCreds,
} from "../lib/apiKeys";

const DEFAULT_AHREFS_KEY = "";

// API keys are now stored server-side per user (encrypted). These keep their original
// names/signatures but read from the synchronous in-memory cache (hydrated on login),
// so the many existing call sites are unchanged.
export function loadAhrefsKey(): string {
  return getAhrefsKey();
}
export function loadAnthropicKey(): string {
  return getAnthropicKey();
}
export function loadDataForSeoCredentials(): { login: string; password: string } {
  return getDfsCreds();
}

interface Props { onClose: () => void; }

type View = "root" | "apis" | "ahrefs" | "claude" | "dataforseo";

// ── Shared styles ─────────────────────────────────────────────────────────────

const rowStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "11px 14px", borderRadius: 6, cursor: "pointer",
  background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)",
  marginBottom: 6, fontFamily: "Mulish, sans-serif", fontSize: 13,
  color: "rgba(255,255,255,.85)", userSelect: "none",
  transition: "background .15s",
};
const chevron = <span style={{ color: "rgba(255,255,255,.3)", fontSize: 16, lineHeight: 1 }}>›</span>;
// These panels render inside the theme-aware Settings dialog (`.st`), so colors read from the
// `--st-*` tokens — light on a light canvas, legible on the dark one. White inputs became
// token fields; teal/green action colors are kept (they read on both grounds).
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 10px", background: "var(--st-field-bg)",
  border: "1px solid var(--st-hair)", borderRadius: 4,
  color: "var(--st-ink)", fontFamily: "Mulish, sans-serif", fontSize: 12,
  outline: "none", boxSizing: "border-box", marginTop: 4,
};
const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" as const,
  color: "var(--st-ink2)", marginTop: 14, marginBottom: 2, display: "block",
  fontFamily: "Mulish, sans-serif",
};
const hintStyle: React.CSSProperties = {
  fontSize: 10, color: "var(--st-ink3)", lineHeight: 1.5, marginBottom: 10,
  fontFamily: "Mulish, sans-serif",
};
const saveBtn = (active: boolean, saved: boolean): React.CSSProperties => ({
  padding: "7px 16px", borderRadius: 0, border: "none", cursor: active ? "pointer" : "default",
  fontFamily: "Mulish, sans-serif", fontSize: 11, fontWeight: 700,
  background: saved ? "#15803d" : active ? "#0f766e" : "var(--st-field-bg)",
  color: active || saved ? "#fff" : "var(--st-ink3)",
  transition: "background .2s",
});

// ── Ahrefs panel ──────────────────────────────────────────────────────────────

export function AhrefsPanel() {
  const init = getAhrefsKey();
  const [draft, setDraft] = useState(init);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const changed = draft.trim() !== getAhrefsKey();

  async function save() {
    const v = draft.trim(); if (!v) return;
    try { await saveAhrefsKey(v); setSaved(true); setTestResult(null); setTimeout(() => setSaved(false), 2000); }
    catch (e) { setTestResult({ ok: false, message: e instanceof Error ? e.message : "Could not save key." }); }
  }
  function reset() { setDraft(DEFAULT_AHREFS_KEY); setTestResult(null); }

  async function testConnection() {
    const key = (getAhrefsKey() || draft).trim();
    if (!key) { setTestResult({ ok: false, message: "Save your API key first." }); return; }
    setTesting(true); setTestResult(null);
    try {
      const res = await fetch("/api/test-ahrefs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: key }),
      });
      const json = await res.json() as { ok: boolean; message: string };
      setTestResult(json);
    } catch {
      setTestResult({ ok: false, message: "Network error — make sure the server is running." });
    } finally {
      setTesting(false);
    }
  }

  return (
    <>
      <p style={hintStyle}>Used for Domain Rating, traffic, search volumes, and organic keyword data.</p>
      <label style={labelStyle}>API Key</label>
      <input style={inputStyle} type="text" value={draft} spellCheck={false} autoComplete="off"
        onChange={e => { setDraft(e.target.value); setSaved(false); setTestResult(null); }} placeholder="Enter Ahrefs API key…" />
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button style={saveBtn(changed, saved)} onClick={save}>{saved ? "✓ Saved" : "Save Key"}</button>
        <button
          onClick={testConnection}
          disabled={testing}
          style={{ ...saveBtn(true, false), background: testing ? "#e2e8f0" : "linear-gradient(135deg,#1d4ed8,#2563eb)", color: testing ? "#94a3b8" : "#fff", opacity: testing ? 0.7 : 1 }}
        >
          {testing ? "Testing…" : "Test Connection"}
        </button>
        {draft !== DEFAULT_AHREFS_KEY && (
          <button style={{ ...saveBtn(true, false), background: "transparent", border: "1px solid var(--st-hair)", color: "var(--st-ink2)" }} onClick={reset}>Reset</button>
        )}
      </div>
      {testResult && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 4, fontFamily: "Mulish, sans-serif", fontSize: 11,
          background: testResult.ok ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.12)",
          border: `1px solid ${testResult.ok ? "rgba(16,185,129,.3)" : "rgba(239,68,68,.3)"}`,
          color: testResult.ok ? "#15803d" : "#dc2626",
        }}>
          {testResult.message}
        </div>
      )}
      {getAhrefsKey() && (
        <div style={{ marginTop: 12, padding: "8px 10px", background: "var(--st-field-bg)", borderRadius: 4, border: "1px solid var(--st-hair)" }}>
          <div style={{ fontSize: 9, color: "var(--st-ink3)", marginBottom: 4, fontFamily: "Mulish, sans-serif" }}>SAVED KEY</div>
          <div style={{ fontSize: 10, color: "var(--st-ink2)", wordBreak: "break-all", fontFamily: "Roboto Mono, monospace", cursor: "text" }}
            onClick={e => { const s = window.getSelection(), r = document.createRange(); r.selectNodeContents(e.currentTarget); s?.removeAllRanges(); s?.addRange(r); }}>
            {getAhrefsKey()}
          </div>
        </div>
      )}
    </>
  );
}

// ── Claude (Anthropic) panel ──────────────────────────────────────────────────

export function ClaudePanel() {
  const init = getAnthropicKey();
  const [draft, setDraft] = useState(init);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const changed = draft.trim() !== getAnthropicKey();

  async function save() {
    const v = draft.trim(); if (!v) return;
    try { await saveAnthropicKey(v); setSaved(true); setTestResult(null); setTimeout(() => setSaved(false), 2000); }
    catch (e) { setTestResult({ ok: false, message: e instanceof Error ? e.message : "Could not save key." }); }
  }
  async function clear() { try { await saveAnthropicKey(""); } catch { /* ignore */ } setDraft(""); setTestResult(null); }

  async function testConnection() {
    const key = (getAnthropicKey() || draft).trim();
    if (!key) { setTestResult({ ok: false, message: "Save your API key first." }); return; }
    setTesting(true); setTestResult(null);
    try {
      const res = await fetch("/api/test-claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: key }),
      });
      const json = await res.json() as { ok: boolean; message: string };
      setTestResult(json);
    } catch {
      setTestResult({ ok: false, message: "Network error — make sure the server is running." });
    } finally {
      setTesting(false);
    }
  }

  return (
    <>
      <p style={hintStyle}>Get your key at console.anthropic.com.</p>
      <label style={labelStyle}>API Key</label>
      <input style={inputStyle} type="text" value={draft} spellCheck={false} autoComplete="off"
        onChange={e => { setDraft(e.target.value); setSaved(false); setTestResult(null); }} placeholder="sk-ant-…" />
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button style={saveBtn(changed, saved)} onClick={save}>{saved ? "✓ Saved" : "Save Key"}</button>
        <button
          onClick={testConnection}
          disabled={testing}
          style={{ ...saveBtn(true, false), background: testing ? "#e2e8f0" : "linear-gradient(135deg,#1d4ed8,#2563eb)", color: testing ? "#94a3b8" : "#fff", opacity: testing ? 0.7 : 1 }}
        >
          {testing ? "Testing…" : "Test Connection"}
        </button>
        {getAnthropicKey() && (
          <button style={{ ...saveBtn(true, false), background: "transparent", border: "1px solid var(--st-hair)", color: "var(--st-ink2)" }} onClick={clear}>Clear</button>
        )}
      </div>
      {testResult && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 4, fontFamily: "Mulish, sans-serif", fontSize: 11,
          background: testResult.ok ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.12)",
          border: `1px solid ${testResult.ok ? "rgba(16,185,129,.3)" : "rgba(239,68,68,.3)"}`,
          color: testResult.ok ? "#15803d" : "#dc2626",
        }}>
          {testResult.message}
        </div>
      )}
    </>
  );
}

// ── DataForSEO panel ──────────────────────────────────────────────────────────

export function DataForSeoPanel() {
  const initCreds = getDfsCreds();
  const [loginDraft,    setLoginDraft]    = useState(initCreds.login);
  const [passwordDraft, setPasswordDraft] = useState(initCreds.password);
  const [saved,    setSaved]    = useState(false);
  const [testing,  setTesting]  = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const cur = getDfsCreds();
  const changed = loginDraft.trim() !== cur.login || passwordDraft.trim() !== cur.password;

  async function save() {
    const l = loginDraft.trim(); const p = passwordDraft.trim();
    if (!l && !p) return;
    try { await saveDfsCreds(l, p); setSaved(true); setTestResult(null); setTimeout(() => setSaved(false), 2000); }
    catch (e) { setTestResult({ ok: false, message: e instanceof Error ? e.message : "Could not save credentials." }); }
  }
  async function clear() {
    try { await saveDfsCreds("", ""); } catch { /* ignore */ }
    setLoginDraft(""); setPasswordDraft(""); setTestResult(null);
  }

  async function testConnection() {
    const cc = getDfsCreds();
    const l = (cc.login || loginDraft).trim();
    const p = (cc.password || passwordDraft).trim();
    if (!l || !p) { setTestResult({ ok: false, message: "Save your credentials first." }); return; }
    setTesting(true); setTestResult(null);
    try {
      const auth = btoa(`${l}:${p}`);
      const res = await fetch("https://api.dataforseo.com/v3/appendix/user_data", {
        headers: { Authorization: `Basic ${auth}` },
      });
      if (res.status === 401) { setTestResult({ ok: false, message: "Invalid credentials — check your API login and password." }); return; }
      if (!res.ok) { setTestResult({ ok: false, message: `HTTP ${res.status} — try again.` }); return; }
      const json = await res.json() as { tasks?: Array<{ result?: Array<{ money?: { balance?: number } }> }> };
      const money = json.tasks?.[0]?.result?.[0]?.money;
      const balance = money?.balance ?? null;
      if (balance !== null) {
        setTestResult({ ok: true, message: `✓ Connected — Account balance: $${Number(balance).toFixed(2)}` });
      } else {
        setTestResult({ ok: true, message: "✓ Connected — credentials valid." });
      }
    } catch {
      setTestResult({ ok: false, message: "Network error — make sure the server is running." });
    } finally {
      setTesting(false);
    }
  }

  return (
    <>
      <p style={hintStyle}>Live Google SERP crawls, competitor discovery, and rank positions. Sign up at dataforseo.com — pay-per-use (~$0.001/search).</p>
      <label style={labelStyle}>API Login (email)</label>
      <input style={inputStyle} type="text" value={loginDraft} spellCheck={false} autoComplete="off"
        onChange={e => { setLoginDraft(e.target.value); setSaved(false); setTestResult(null); }} placeholder="your@email.com" />
      <label style={{ ...labelStyle, marginTop: 10 }}>API Password</label>
      <input style={inputStyle} type="text" value={passwordDraft} spellCheck={false} autoComplete="off"
        onChange={e => { setPasswordDraft(e.target.value); setSaved(false); setTestResult(null); }} placeholder="DataForSEO API password" />
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button style={saveBtn(changed, saved)} onClick={save}>{saved ? "✓ Saved" : "Save Credentials"}</button>
        <button
          onClick={testConnection}
          disabled={testing}
          style={{ ...saveBtn(true, false), background: testing ? "#e2e8f0" : "linear-gradient(135deg,#1d4ed8,#2563eb)", color: testing ? "#94a3b8" : "#fff", opacity: testing ? 0.7 : 1 }}
        >
          {testing ? "Testing…" : "Test Connection"}
        </button>
        {(cur.login || cur.password) && (
          <button style={{ ...saveBtn(true, false), background: "transparent", border: "1px solid var(--st-hair)", color: "var(--st-ink2)" }} onClick={clear}>Clear</button>
        )}
      </div>
      {testResult && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 4, fontFamily: "Mulish, sans-serif", fontSize: 11,
          background: testResult.ok ? "rgba(16,185,129,.12)" : "rgba(239,68,68,.12)",
          border: `1px solid ${testResult.ok ? "rgba(16,185,129,.3)" : "rgba(239,68,68,.3)"}`,
          color: testResult.ok ? "#15803d" : "#dc2626",
        }}>
          {testResult.message}
        </div>
      )}
      {cur.login && (
        <div style={{ marginTop: 10, padding: "8px 10px", background: "var(--st-field-bg)", borderRadius: 4, border: "1px solid var(--st-hair)" }}>
          <div style={{ fontSize: 9, color: "var(--st-ink3)", marginBottom: 2, fontFamily: "Mulish, sans-serif" }}>SAVED LOGIN</div>
          <div style={{ fontSize: 11, color: "var(--st-ink2)", fontFamily: "Mulish, sans-serif" }}>{cur.login}</div>
        </div>
      )}
    </>
  );
}

export function BrightDataPanel() {
  const init = getBrightDataCreds();
  const [tokenDraft, setTokenDraft] = useState(init.token);
  const [zoneDraft, setZoneDraft] = useState(init.zone);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cur = getBrightDataCreds();
  const changed = tokenDraft.trim() !== cur.token || zoneDraft.trim() !== cur.zone;

  async function save() {
    const t = tokenDraft.trim(); const z = zoneDraft.trim();
    if (!t && !z) return;
    try { await saveBrightDataCreds(t, z); setSaved(true); setErr(null); setTimeout(() => setSaved(false), 2000); }
    catch (e) { setErr(e instanceof Error ? e.message : "Could not save credentials."); }
  }
  async function clear() {
    try { await saveBrightDataCreds("", ""); } catch { /* ignore */ }
    setTokenDraft(""); setZoneDraft(""); setErr(null);
  }

  return (
    <>
      <p style={hintStyle}>Powers live Google ad discovery in the Niche Ads Scraper (residential rendering that captures the ads a real browser sees). Create a Web Scraper / SERP API zone at brightdata.com, then paste its API token and zone name.</p>
      <label style={labelStyle}>API Token</label>
      <input style={inputStyle} type="text" value={tokenDraft} spellCheck={false} autoComplete="off"
        onChange={e => { setTokenDraft(e.target.value); setSaved(false); setErr(null); }} placeholder="Bright Data API token" />
      <label style={{ ...labelStyle, marginTop: 10 }}>Zone</label>
      <input style={inputStyle} type="text" value={zoneDraft} spellCheck={false} autoComplete="off"
        onChange={e => { setZoneDraft(e.target.value); setSaved(false); setErr(null); }} placeholder="e.g. serp_api1" />
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button style={saveBtn(changed, saved)} onClick={save}>{saved ? "✓ Saved" : "Save Credentials"}</button>
        {(cur.token || cur.zone) && (
          <button style={{ ...saveBtn(true, false), background: "transparent", border: "1px solid var(--st-hair)", color: "var(--st-ink2)" }} onClick={clear}>Clear</button>
        )}
      </div>
      {err && (
        <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 4, fontFamily: "Mulish, sans-serif", fontSize: 11, background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.3)", color: "#dc2626" }}>{err}</div>
      )}
      {cur.zone && (
        <div style={{ marginTop: 10, padding: "8px 10px", background: "var(--st-field-bg)", borderRadius: 4, border: "1px solid var(--st-hair)" }}>
          <div style={{ fontSize: 9, color: "var(--st-ink3)", marginBottom: 2, fontFamily: "Mulish, sans-serif" }}>SAVED ZONE</div>
          <div style={{ fontSize: 11, color: "var(--st-ink2)", fontFamily: "Mulish, sans-serif" }}>{cur.zone}</div>
        </div>
      )}
    </>
  );
}

// ── Modal ─────────────────────────────────────────────────────────────────────

const VIEW_META: Record<string, { title: string; back: View }> = {
  apis:       { title: "APIs",              back: "root"       },
  ahrefs:     { title: "Ahrefs API Key",    back: "apis"       },
  claude:     { title: "Claude API Key",    back: "apis"       },
  dataforseo: { title: "DataForSEO API",    back: "apis"       },
};

function ResetAuditRow() {
  const [confirmed, setConfirmed] = useState(false);
  const hasData = !!(localStorage.getItem("full_audit_sections") || localStorage.getItem("seo_audit_ever_run"));

  function reset() {
    localStorage.removeItem("full_audit_sections");
    localStorage.removeItem("full_audit_analyses");
    localStorage.removeItem("seo_audit_ever_run");
    setConfirmed(true);
    setTimeout(() => window.location.reload(), 800);
  }

  return (
    <div style={{ ...rowStyle, cursor: hasData ? "pointer" : "default", opacity: hasData ? 1 : 0.4,
      borderColor: confirmed ? "rgba(239,68,68,.4)" : "rgba(255,255,255,.08)",
      background: confirmed ? "rgba(239,68,68,.1)" : "rgba(255,255,255,.04)",
    }}
      onMouseEnter={e => { if (hasData) e.currentTarget.style.background = confirmed ? "rgba(239,68,68,.15)" : "rgba(255,255,255,.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = confirmed ? "rgba(239,68,68,.1)" : "rgba(255,255,255,.04)"; }}
      onClick={() => { if (!hasData || confirmed) return; void showConfirm("Clear all generated audit data and reset tabs to static content?", { title: "Reset Audit Data", confirmLabel: "Clear & Reset", tone: "danger" }).then(ok => { if (ok) reset(); }); }}
    >
      <div>
        <div style={{ fontWeight: 700, color: confirmed ? "#f87171" : "rgba(255,255,255,.85)" }}>
          {confirmed ? "✓ Cleared — reloading…" : "Reset Audit Data"}
        </div>
        <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", marginTop: 2, fontFamily: "Mulish, sans-serif" }}>
          {hasData ? "Clear generated sections · re-run from scratch" : "No audit data stored"}
        </div>
      </div>
      <span style={{ color: "rgba(239,68,68,.6)", fontSize: 14 }}>↺</span>
    </div>
  );
}

function hasDfs() { return !!getDfsCreds().login; }
function hasAhrefs() { return !!getAhrefsKey(); }
function hasClaude() { return !!getAnthropicKey(); }

export function SettingsModal({ onClose }: Props) {
  const [view, setView] = useState<View>("root");
  const meta = view !== "root" ? VIEW_META[view] : null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={e => e.stopPropagation()} style={{ minWidth: 340, maxWidth: 400 }}>

        {/* Header */}
        <div className="settings-header">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {meta && (
              <button onClick={() => setView(meta.back)}
                style={{ background: "none", border: "none", color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px 0 0", borderRadius: 0 }}>
                ‹
              </button>
            )}
            <span style={{ fontFamily: "'Oswald', 'Barlow Condensed', 'Inter', sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>{meta ? meta.title : "⚙ Settings"}</span>
          </div>
          <button className="settings-close" onClick={onClose} style={{ borderRadius: 0 }}>✕</button>
        </div>

        {/* Body */}
        <div className="settings-body">

          {view === "root" && (
            <>
              <div
                style={{ ...rowStyle }}
                onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,.08)")}
                onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,.04)")}
                onClick={() => setView("apis")}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>APIs</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", marginTop: 2, fontFamily: "Mulish, sans-serif" }}>
                    Ahrefs · Claude · DataForSEO
                  </div>
                </div>
                {chevron}
              </div>
              <ResetAuditRow />
            </>
          )}

          {view === "apis" && (
            <>
              {(["ahrefs", "claude", "dataforseo"] as const).map((key) => {
                const labels = { ahrefs: "Ahrefs", claude: "Claude (Anthropic)", dataforseo: "DataForSEO" };
                const hints  = {
                  ahrefs:     hasAhrefs()  ? "Key saved ✓" : "Not configured",
                  claude:     hasClaude()  ? "Key saved ✓" : "Not configured",
                  dataforseo: hasDfs()     ? "Credentials saved ✓" : "Not configured",
                };
                const configured = { ahrefs: hasAhrefs(), claude: hasClaude(), dataforseo: hasDfs() };
                return (
                  <div key={key}
                    style={{ ...rowStyle }}
                    onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,.08)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,.04)")}
                    onClick={() => setView(key)}
                  >
                    <div>
                      <div style={{ fontWeight: 700 }}>{labels[key]}</div>
                      <div style={{ fontSize: 10, color: configured[key] ? "rgba(16,185,129,.7)" : "rgba(255,255,255,.3)", marginTop: 2, fontFamily: "Mulish, sans-serif" }}>
                        {hints[key]}
                      </div>
                    </div>
                    {chevron}
                  </div>
                );
              })}
            </>
          )}

          {view === "ahrefs"     && <AhrefsPanel />}
          {view === "claude"     && <ClaudePanel />}
          {view === "dataforseo" && <DataForSeoPanel />}

        </div>
      </div>
    </div>
  );
}
